import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Plus, Trash2, Video, Link2, Image, File, Upload, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _uid = 0;
const nid = () => `ev-${Date.now()}-${_uid++}`;

const KIND_META = {
  video: { icon: Video, label: 'Video' },
  link: { icon: Link2, label: 'Link' },
  image: { icon: Image, label: 'Image' },
  file: { icon: File, label: 'File / PDF' },
};

/**
 * Report-OWNED supporting evidence editor. Produces items shaped like the backend
 * ReportEvidence model: { id, kind, label, url, source, timestamp, caption, order,
 * object_key, file_name, include_in_final }.
 * Never touches Match Analysis / source evidence.
 */
export const ReportEvidenceEditor = ({ items, onChange, reportId, testId = 'report-evidence-editor' }) => {
  const list = items || [];
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});
  const add = (kind) => onChange([...list, {
    id: nid(), kind, label: '', url: '', source: kind === 'video' ? 'Veo' : '', timestamp: '',
    caption: '', order: list.length, include_in_final: true,
  }]);
  const patch = (id, p) => onChange(list.map((e) => (e.id === id ? { ...e, ...p } : e)));
  const remove = (id) => onChange(list.filter((e) => e.id !== id).map((e, i) => ({ ...e, order: i })));
  const uploadImage = async (id, file) => {
    if (!file || !reportId) { if (!reportId) toast.error('Save the report first, then upload'); return; }
    setUploadingId(id);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/development-reports/${reportId}/evidence-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      patch(id, { url: res.data.url, object_key: res.data.object_key, file_name: res.data.file_name, label: list.find((e) => e.id === id)?.label || res.data.file_name });
      toast.success('Image uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingId(null); }
  };
  const uploadFile = async (id, file) => {
    if (!file || !reportId) { if (!reportId) toast.error('Save the report first, then upload'); return; }
    setUploadingId(id);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API}/development-reports/${reportId}/evidence-file`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(list.map((e) => (e.id === id ? { ...e, url: res.data.url, object_key: res.data.object_key, file_name: res.data.file_name, label: e.label || res.data.file_name } : e)));
      toast.success('File uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingId(null); }
  };
  return (
    <div className="mt-2" data-testid={testId}>
      <div className="mb-1 text-xs font-medium text-slate-500">Supporting Evidence <span className="text-slate-400">(report-owned — not written back to Match Analysis)</span></div>
      <div className="space-y-2">
        {list.map((e, i) => {
          const M = KIND_META[e.kind] || KIND_META.video;
          const Icon = M.icon;
          return (
            <div key={e.id} className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-2" data-testid={`${testId}-item-${i}`}>
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500"><Icon className="h-3.5 w-3.5" /> {M.label}
                <label className="ml-2 inline-flex items-center gap-1 text-[11px] font-normal text-slate-500 cursor-pointer" title="Shown in the finalized/exported document unless unticked">
                  <input type="checkbox" checked={e.include_in_final !== false}
                    onChange={(ev) => patch(e.id, { include_in_final: ev.target.checked })}
                    data-testid={`${testId}-include-final-${i}`} />
                  Include in Final Report
                </label>
                <button type="button" onClick={() => remove(e.id)} className="ml-auto text-slate-300 hover:text-rose-600" data-testid={`${testId}-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                <Input value={e.label || ''} onChange={(ev) => patch(e.id, { label: ev.target.value })} placeholder="Label / title" data-testid={`${testId}-label-${i}`} />
                {(e.kind === 'video' || e.kind === 'link') && <Input value={e.url || ''} onChange={(ev) => patch(e.id, { url: ev.target.value })} placeholder="Video / link URL" data-testid={`${testId}-url-${i}`} />}
                {e.kind !== 'image' && e.kind !== 'file' && <Input value={e.timestamp || ''} onChange={(ev) => patch(e.id, { timestamp: ev.target.value })} placeholder="Timestamp (e.g. 42:17)" data-testid={`${testId}-ts-${i}`} />}
                <Input value={e.caption || ''} onChange={(ev) => patch(e.id, { caption: ev.target.value })} placeholder="Caption / context (optional)" data-testid={`${testId}-caption-${i}`} />
              </div>
              {e.kind === 'image' && (
                <div className="mt-1.5">
                  <input ref={(el) => { fileRefs.current[e.id] = el; }} type="file" accept="image/*" className="hidden" onChange={(ev) => uploadImage(e.id, ev.target.files?.[0])} />
                  <Button variant="outline" size="sm" onClick={() => fileRefs.current[e.id]?.click()} disabled={uploadingId === e.id} data-testid={`${testId}-upload-${i}`}>
                    {uploadingId === e.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1 h-3.5 w-3.5" />} Upload image
                  </Button>
                  {e.url && <span className="ml-2 text-[11px] text-emerald-600">image attached</span>}
                </div>
              )}
              {e.kind === 'file' && (
                <div className="mt-1.5">
                  <input ref={(el) => { fileRefs.current[e.id] = el; }} type="file" accept=".pdf,.doc,.docx,.xlsx,.csv,.txt" className="hidden" onChange={(ev) => uploadFile(e.id, ev.target.files?.[0])} />
                  <Button variant="outline" size="sm" onClick={() => fileRefs.current[e.id]?.click()} disabled={uploadingId === e.id} data-testid={`${testId}-upload-file-${i}`}>
                    {uploadingId === e.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1 h-3.5 w-3.5" />} Upload file / PDF
                  </Button>
                  {e.url && <span className="ml-2 text-[11px] text-emerald-600">{e.file_name || 'file'} attached</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => add('video')} data-testid={`${testId}-add-video`}><Plus className="mr-1 h-3.5 w-3.5" /> Video</Button>
        <Button variant="outline" size="sm" onClick={() => add('link')} data-testid={`${testId}-add-link`}><Plus className="mr-1 h-3.5 w-3.5" /> Link</Button>
        <Button variant="outline" size="sm" onClick={() => add('image')} data-testid={`${testId}-add-image`}><Plus className="mr-1 h-3.5 w-3.5" /> Image</Button>
        <Button variant="outline" size="sm" onClick={() => add('file')} data-testid={`${testId}-add-file`}><Plus className="mr-1 h-3.5 w-3.5" /> File / PDF</Button>
      </div>
    </div>
  );
};

export default ReportEvidenceEditor;
